import axios from "axios"

import { ERROR_MESSAGE_KEYS } from "../constants/error-messages"
import type { TransportFailureContext } from "../observability/transport-failures"

export const AUTH_URL = "auth/token/refresh/"
export const VERIFY_URL = "auth/token/verify/"

const EXCLUDED_URLS = ["auth/whatsapp-login", "auth/google-login"]

const SILENT_NETWORK_ERROR_URLS = ["auth/session-activity"]

export const REQUEST_STARTED_AT = "__eadRequestStartedAt"

interface AuthErrorHandlerDeps {
  refresh: () => Promise<{ access: string }>
  retry: (config: unknown) => Promise<unknown>
  logout: () => void
  saveNetworkError: (err: unknown) => void
  reportTransportFailure: (failure: TransportFailureContext) => void
  now?: () => number
}

export const normalizeApiPath = (url?: string | null): string =>
  (url ?? "").replace(/^\/+/, "")

export const createAuthRequestInterceptor =
  ({
    getAccessToken,
    now = Date.now
  }: {
    getAccessToken: () => string | null
    now?: () => number
  }) =>
  (req: any): any => {
    req[REQUEST_STARTED_AT] = now()
    const accessToken = getAccessToken()
    req.headers.Authorization = accessToken ? `JWT ${accessToken}` : null

    if (
      [AUTH_URL, VERIFY_URL].includes(normalizeApiPath(req.url)) &&
      req.headers.Authorization
    ) {
      delete req.headers.Authorization
    }

    return req
  }

export const createAuthResponseErrorHandler =
  ({
    refresh,
    retry,
    logout,
    saveNetworkError,
    reportTransportFailure,
    now = Date.now
  }: AuthErrorHandlerDeps) =>
  async (err: any): Promise<unknown> => {
    const status = err?.response?.status ?? null
    const config = err?.config ?? {}
    const url = normalizeApiPath(config.url)

    if (axios.isCancel(err) || err?.code === "ERR_CANCELED") {
      return Promise.reject(err)
    }

    if (!status) {
      if (!SILENT_NETWORK_ERROR_URLS.some((silent) => url.includes(silent))) {
        const startedAt = config[REQUEST_STARTED_AT]
        try {
          reportTransportFailure({
            url: config.url,
            method: config.method,
            durationMs:
              typeof startedAt === "number" ? Math.max(0, now() - startedAt) : 0
          })
        } catch {
          // Observability must never replace the original request failure.
        }
        saveNetworkError(ERROR_MESSAGE_KEYS.network)
      }
      return Promise.reject(err)
    }

    if (EXCLUDED_URLS.some((excluded) => url.includes(excluded))) {
      return Promise.reject(err)
    }

    // Refresh and verify recover through their own thunks
    if (url === AUTH_URL || url === VERIFY_URL) {
      return Promise.reject(err)
    }

    if (status !== 401) {
      return Promise.reject(err)
    }

    if (config.__authRetried) {
      logout()
      return Promise.reject(err)
    }
    config.__authRetried = true

    let access: string
    try {
      ;({ access } = await refresh())
    } catch {
      return Promise.reject(err)
    }

    config.headers = { ...config.headers, Authorization: `JWT ${access}` }
    return retry(config)
  }
